import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { AppHeader } from '../components/AppHeader';
import { Spinner } from '../components/Spinner';
import { useAgenda } from '../api/queries';
import { api } from '../api/client';
import type { SpacingProfile } from '../types/agenda';
import { AccountMenu } from '../auth/AccountMenu';
import './preview.css';

export function PreviewScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: agenda, isLoading, isError, error } = useAgenda(id);

  const [profile, setProfile] = useState<SpacingProfile>('medium');
  const [size, setSize] = useState<'letter' | '13x19'>('letter');

  // Use the agenda's spacingProfile if available, otherwise default to 'medium'
  useEffect(() => {
    if (agenda?.spacingProfile) {
      setProfile(agenda.spacingProfile);
    }
  }, [agenda]);

  const [pages, setPages] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const fetchPreview = async () => {
      setIsGenerating(true);
      try {
        const result = await api.previewAllPngs(id, { profile, size });
        if (isMounted) setPages(result.pages);
      } catch (e) {
        console.error('Failed to fetch preview:', e);
        if (isMounted) setPages([]);
      } finally {
        if (isMounted) setIsGenerating(false);
      }
    };
    fetchPreview();
    return () => { isMounted = false; };
  }, [id, profile, size]);

  if (isLoading) {
    return (
      <Screen header={<AppHeader title="Loading…" back={`/agenda/${id}/edit`} actions={<AccountMenu />} />}>
        <Spinner />
      </Screen>
    );
  }

  if (isError || !agenda) {
    return (
      <Screen header={<AppHeader title="Preview" back={`/agenda/${id}/edit`} actions={<AccountMenu />} />}>
        <div className="state-center">
          <div className="state-icon">⚠️</div>
          <h2>Couldn't load agenda</h2>
          <p>{(error as Error)?.message}</p>
          <button className="btn" onClick={() => navigate(`/agenda/${id}/edit`)}>
            Back
          </button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      header={<AppHeader title="PDF Preview" back={`/agenda/${id}/edit`} actions={<AccountMenu />} />}

      contentClassName="preview-screen"
    >
      <div className="preview-controls">
        <div className="control-group">
          <label className="control-label">Spacing</label>
          <div className="segmented">
            {(['compact', 'medium', 'loose'] as const).map((p) => (
              <button
                key={p}
                className={`segment${profile === p ? ' active' : ''}`}
                onClick={() => setProfile(p)}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <label className="control-label">Size</label>
          <div className="segmented">
            {(['letter', '13x19'] as const).map((s) => (
              <button
                key={s}
                className={`segment${size === s ? ' active' : ''}`}
                onClick={() => setSize(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {agenda.spacingProfile && (
          <p className="preview-note">
            💾 Preview reflects the last saved version (spacing: {agenda.spacingProfile})
          </p>
        )}
      </div>

      <div className="preview-container">
        {isGenerating ? (
          <Spinner label="Generating preview…" />
        ) : pages.length > 0 ? (
          <div className="preview-pages">
            {pages.map((b64, i) => (
              <div key={i} className="preview-page-wrapper">
                <img
                  src={`data:image/png;base64,${b64}`}
                  alt={`Page ${i + 1}`}
                  className="preview-page-image"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="state-center">
            <p>Preview unavailable</p>
          </div>
        )}
      </div>
    </Screen>
  );
}
